/* @jsx h */
import { defineComponent, h } from "vue";
import { CustomerCheckout } from "@lemmonhq/types";
import { calculateCartSubtotal } from "@lemmonhq/orders";
import { useMenu } from "@app/vue/providers/Menu";
import { getCharacterNameForCustomer } from "../../../pages/ShopGroupOrder/utils/packs";
import { useLocalization } from "@lemmonhq/uikit";

export interface SubmittedOrderCardProps {
  checkout: CustomerCheckout;
}

export const SubmittedOrderCard = defineComponent<SubmittedOrderCardProps>({
  name: "SubmittedOrderCard",
  props: ["checkout"],
  setup(props) {
    const { realtime, groupOrder, shop } = useMenu();
    const { t } = useLocalization();
    const formatTime = (dateString: string) => {
      return new Date(dateString).toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    return () => {
      const groupMembers = realtime.groupState.value.members;
      // Find the member from groupMembers using customer_id
      const member = groupMembers.find(
        (m) => m.clientId === props.checkout.customer_id,
      );

      // Get character name for this customer
      const characterName = groupOrder.value
        ? getCharacterNameForCustomer(
            groupOrder.value,
            props.checkout.customer_id,
          )
        : null;

      const memberName =
        characterName ||
        member?.clientName ||
        t("customer.checkout_status.unknown_member");
      const memberInitial = memberName.split(" ")[0];

      return (
        <div class="rounded bg-base-200 shadow-lg hover:shadow-xl border rounded-lg ">
          <div class={`p-3 lg:p-4`}>
            <div class="flex items-center justify-between mb-3">
              <div class="flex items-center gap-3">
                <div class="avatar placeholder hidden">
                  <div class="bg-success text-success-content rounded-full shadow-md w-10 h-10 flex justify-center items-center">
                    <span class="text-lg font-bold">{memberInitial}</span>
                  </div>
                </div>
                <div>
                  <div class={`font-bold`}>
                    {memberName}{" "}
                    <span class="hidden">#{props.checkout.id.slice(0, 8)}</span>
                  </div>
                  <div class="flex gap-2">
                    <div class="text-xs text-base-content/60 flex items-center gap-1">
                      {formatTime(props.checkout.created_at)}
                    </div>
                    <div class="text-xs text-base-content/60 flex items-center gap-1 uppercase">
                      {" "}
                      |
                    </div>
                    <div class="text-xs text-base-content/60 flex items-center gap-1 uppercase">
                      {props.checkout.payment_method}
                    </div>
                  </div>
                </div>
              </div>
              <div class="text-right">
                <div class={`font-bold `}>
                  {t(
                    props.checkout.payload.cart.items.length === 1
                      ? "customer.checkout_status.item_count_one"
                      : "customer.checkout_status.item_count_many",
                    [props.checkout.payload.cart.items.length],
                  )}
                </div>
                <div class="text-xs text-base-content/60">
                  {shop.value.currency}{" "}
                  {calculateCartSubtotal(props.checkout.payload.cart).toFixed(
                    2,
                  )}
                </div>
              </div>
            </div>

            {props.checkout.payload.cart.items.length > 0 && (
              <div class="mt-2 pt-3 border-success/20">
                <div class="text-1xl text-accent  mb-2 flex items-center gap-1">
                  <span class="material-symbols-outlined text-1xl">
                    restaurant
                  </span>
                  {t("customer.checkout_status.items")}
                </div>
                <div class="flex flex-wrap gap-1">
                  {props.checkout.payload.cart.items.map((item, itemIndex) => (
                    <span key={itemIndex} class="text-sm">
                      {item.quantity}× {item.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      );
    };
  },
});
